import { useState } from 'react';

const AWSArchitectureGuide = () => {
  const [activeTab, setActiveTab] = useState('regions');
  const [selectedRegion, setSelectedRegion] = useState('us-east-1');
  const [rtoCalculator, setRtoCalculator] = useState({ downtime: 60, cost: 100000 });
  const [rpoCalculator, setRpoCalculator] = useState({ dataLoss: 15, hourlyData: 10000 });
  const [theme, setTheme] = useState('dark');

  // Theme styles
  const themeStyles = {
    light: {
      '--bg-gradient': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      '--container-bg': 'white',
      '--text-primary': '#2c3e50',
      '--text-secondary': '#5d6d7e',
      '--text-muted': '#7f8c8d',
      '--section-bg': '#f8f9fa',
      '--card-bg': 'white',
      '--border-color': '#e9ecef',
      '--shadow': 'rgba(0,0,0,0.1)'
    },
    dark: {
      '--bg-gradient': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      '--container-bg': '#1e1e2e',
      '--text-primary': '#e2e8f0',
      '--text-secondary': '#cbd5e1',
      '--text-muted': '#94a3b8',
      '--section-bg': '#262640',
      '--card-bg': '#2d2d44',
      '--border-color': '#374151',
      '--shadow': 'rgba(0,0,0,0.3)'
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'var(--bg-gradient)',
      minHeight: '100vh',
      padding: '20px',
      transition: 'all 0.3s ease',
      ...themeStyles[theme as keyof typeof themeStyles]
    },
    mainContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'var(--container-bg)',
      borderRadius: '15px',
      boxShadow: '0 20px 40px var(--shadow)',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    header: {
      background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
      color: 'white',
      padding: '30px',
      textAlign: 'center' as const,
      position: 'relative' as const
    },
    themeToggle: {
      position: 'absolute' as const,
      top: '30px',
      right: '30px',
      background: 'rgba(255,255,255,0.1)',
      border: '2px solid rgba(255,255,255,0.2)',
      borderRadius: '25px',
      padding: '8px 16px',
      color: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    content: {
      padding: '30px'
    },
    tabContainer: {
      display: 'flex',
      marginBottom: '30px',
      background: 'var(--section-bg)',
      borderRadius: '10px',
      padding: '5px'
    },
    tab: {
      flex: 1,
      padding: '15px 20px',
      textAlign: 'center' as const,
      cursor: 'pointer',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      color: 'var(--text-secondary)',
      fontWeight: '600'
    },
    activeTab: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    },
    section: {
      background: 'var(--section-bg)',
      padding: '25px',
      borderRadius: '10px',
      marginBottom: '20px',
      transition: 'all 0.3s ease'
    },
    card: {
      background: 'var(--card-bg)',
      padding: '20px',
      borderRadius: '10px',
      marginBottom: '15px',
      border: '2px solid var(--border-color)',
      transition: 'all 0.3s ease'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      marginBottom: '20px'
    },
    azContainer: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '20px',
      background: 'var(--section-bg)',
      borderRadius: '10px',
      marginBottom: '20px'
    },
    azBox: {
      width: '120px',
      height: '80px',
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      margin: '0 10px',
      position: 'relative' as const
    },
    connectionLine: {
      height: '2px',
      background: 'var(--text-muted)',
      flex: 1,
      position: 'relative' as const
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '2px solid var(--border-color)',
      borderRadius: '8px',
      fontSize: '16px',
      background: 'var(--card-bg)',
      color: 'var(--text-primary)',
      marginBottom: '10px'
    },
    button: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '12px 25px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  const awsRegions = {
    'us-east-1': { name: 'N. Virginia', azs: ['us-east-1a', 'us-east-1b', 'us-east-1c', 'us-east-1d', 'us-east-1e', 'us-east-1f'] },
    'us-west-2': { name: 'Oregon', azs: ['us-west-2a', 'us-west-2b', 'us-west-2c', 'us-west-2d'] },
    'eu-west-1': { name: 'Ireland', azs: ['eu-west-1a', 'eu-west-1b', 'eu-west-1c'] },
    'ap-southeast-1': { name: 'Singapore', azs: ['ap-southeast-1a', 'ap-southeast-1b', 'ap-southeast-1c'] },
    'ap-south-1': { name: 'Mumbai, India', azs: ['ap-south-1a', 'ap-south-1b', 'ap-south-1c'] },
    'ap-south-2': { name: 'Hyderabad, India', azs: ['ap-south-2a', 'ap-south-2b', 'ap-south-2c'] }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const renderRegionsTab = () => (
    <div>
      <div style={styles.section}>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '10px' }}>🌍</span>
          AWS Regions
        </h2>
        
        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>What is an AWS Region?</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '15px' }}>
            An AWS Region is a <strong>geographical area</strong> that contains multiple, isolated data centers called Availability Zones. 
            Each region is designed to be completely isolated from other regions to achieve fault tolerance and stability.
          </p>
          
          <div style={{ marginBottom: '15px' }}>
            <a 
              href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/using-regions-availability-zones.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📖 AWS Official Documentation: Regions and Availability Zones →
            </a>
          </div>
          
          <div style={{ background: 'var(--section-bg)', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
            <h4 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>Key Characteristics:</h4>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginLeft: '20px' }}>
              <li><strong>Geographic Separation:</strong> Hundreds of miles apart for disaster resilience</li>
              <li><strong>Data Sovereignty:</strong> Data doesn't leave the region unless explicitly moved</li>
              <li><strong>Service Availability:</strong> Not all AWS services are available in every region</li>
              <li><strong>Latency Optimization:</strong> Choose regions closest to your users</li>
              <li><strong>Pricing Variation:</strong> Costs differ between regions</li>
            </ul>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Interactive Region Explorer</h3>
          
          <div style={{ marginBottom: '15px' }}>
            <a 
              href="https://aws.amazon.com/about-aws/global-infrastructure/regions_az/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px',
                marginRight: '20px'
              }}
            >
              📖 AWS Global Infrastructure →
            </a>
            <a 
              href="https://aws.amazon.com/about-aws/global-infrastructure/localzones/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📖 AWS Local Zones →
            </a>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px', display: 'block' }}>
              Select a Region:
            </label>
            <select 
              style={styles.input}
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
            >
              {Object.entries(awsRegions).map(([code, info]) => (
                <option key={code} value={code}>{code} - {info.name}</option>
              ))}
            </select>
          </div>
          
          <div style={{
            background: 'var(--section-bg)',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center' as const
          }}>
            <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>
              Region: {selectedRegion} ({awsRegions[selectedRegion as keyof typeof awsRegions].name})
            </h4>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
              Availability Zones: {awsRegions[selectedRegion as keyof typeof awsRegions].azs.length}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
              {awsRegions[selectedRegion as keyof typeof awsRegions].azs.map((az) => (
                <div key={az} style={{
                  background: '#3498db',
                  color: 'white',
                  padding: '8px 15px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {az}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.gridContainer}>
          {[
            {
              title: '🎯 Compliance',
              content: 'Certain data must stay within specific geographic boundaries due to regulations like GDPR, HIPAA, or local data protection laws.'
            },
            {
              title: '⚡ Latency',
              content: 'Choose regions closest to your users. Each 1000 miles adds ~10ms latency. Critical for real-time applications.'
            },
            {
              title: '💰 Cost',
              content: 'Pricing varies significantly between regions. US East (N. Virginia) is typically the cheapest.'
            },
            {
              title: '🛡️ Disaster Recovery',
              content: 'Multi-region deployments protect against region-wide outages, natural disasters, or geopolitical issues.'
            }
          ].map((item, idx) => (
            <div key={idx} style={styles.card}>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>{item.title}</h4>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAZsTab = () => (
    <div>
      <div style={styles.section}>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '10px' }}>🏢</span>
          Availability Zones (AZs)
        </h2>
        
        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>What is an Availability Zone?</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '15px' }}>
            An Availability Zone is <strong>one or more data centers</strong> within a region that are isolated from failures 
            in other AZs. AZs are connected by high-bandwidth, low-latency networking and are located far enough apart 
            to limit the impact of local failures.
          </p>
          
          <div style={{ marginBottom: '15px' }}>
            <a 
              href="https://aws.amazon.com/about-aws/global-infrastructure/availability-zones/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📖 Learn more about AWS Availability Zones →
            </a>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>AZ Architecture Visualization</h3>
          <div style={styles.azContainer}>
            <div style={styles.azBox}>
              <div>
                <div style={{ fontSize: '12px' }}>AZ-A</div>
                <div style={{ fontSize: '10px' }}>Data Center 1</div>
              </div>
            </div>
            
            <div style={styles.connectionLine}>
              <div style={{
                position: 'absolute' as const,
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--card-bg)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}>
                &lt; 2ms latency
              </div>
            </div>
            
            <div style={styles.azBox}>
              <div>
                <div style={{ fontSize: '12px' }}>AZ-B</div>
                <div style={{ fontSize: '10px' }}>Data Center 2</div>
              </div>
            </div>
            
            <div style={styles.connectionLine}>
              <div style={{
                position: 'absolute' as const,
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--card-bg)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}>
                &lt; 2ms latency
              </div>
            </div>
            
            <div style={styles.azBox}>
              <div>
                <div style={{ fontSize: '12px' }}>AZ-C</div>
                <div style={{ fontSize: '10px' }}>Data Center 3</div>
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center' as const, color: 'var(--text-muted)', fontSize: '14px' }}>
            AZs within a region are connected by redundant, high-speed networking
          </div>
        </div>

        <div style={styles.gridContainer}>
          {[
            {
              title: '🔒 Isolation',
              details: [
                'Separate power grids',
                'Independent cooling systems', 
                'Dedicated network infrastructure',
                'Physical security barriers'
              ]
            },
            {
              title: '🌐 Connectivity',
              details: [
                'Sub-millisecond latency between AZs',
                'Redundant fiber connections',
                'High bandwidth (25+ Gbps)',
                'Automatic failover capabilities'
              ]
            },
            {
              title: '📍 Physical Distance',
              details: [
                'Miles apart within a region',
                'Protected from local disasters',
                'Flood zone separation',
                'Different utility providers'
              ]
            },
            {
              title: '⚙️ Best Practices',
              details: [
                'Deploy across multiple AZs',
                'Use ELB for traffic distribution',
                'Replicate data across AZs',
                'Monitor AZ health metrics'
              ],
              links: [
                {
                  text: '📖 AWS Well-Architected Framework →',
                  url: 'https://docs.aws.amazon.com/wellarchitected/latest/framework/welcome.html'
                },
                {
                  text: '📖 High Availability and Scalability →',
                  url: 'https://docs.aws.amazon.com/whitepapers/latest/real-time-communication-on-aws/high-availability-and-scalability-on-aws.html'
                }
              ]
            }
          ].map((item, idx) => (
            <div key={idx} style={styles.card}>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>{item.title}</h4>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginLeft: '20px' }}>
                {item.details.map((detail, i) => (
                  <li key={i}>{detail}</li>
                ))}
              </ul>
              {item.links && (
                <div style={{ marginTop: '15px' }}>
                  {item.links.map((link, i) => (
                    <a 
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#3498db', 
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        marginRight: '20px',
                        display: 'inline-block',
                        marginBottom: '5px'
                      }}
                    >
                      {link.text}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Multi-AZ Deployment Strategies</h3>
          <div style={styles.gridContainer}>
            <div style={{
              background: 'var(--section-bg)',
              padding: '20px',
              borderRadius: '10px',
              border: '2px solid #27ae60'
            }}>
              <h4 style={{ color: '#27ae60', marginBottom: '15px' }}>✅ High Availability</h4>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Deploy identical infrastructure across 2+ AZs. If one AZ fails, traffic automatically routes to healthy AZs.
                <strong> Availability: 99.99%+</strong>
              </p>
            </div>
            <div style={{
              background: 'var(--section-bg)',
              padding: '20px',
              borderRadius: '10px',
              border: '2px solid #3498db'
            }}>
              <h4 style={{ color: '#3498db', marginBottom: '15px' }}>🔄 Load Balancing</h4>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Distribute traffic across AZs using Application Load Balancer. Provides both performance and resilience benefits.
                <strong> Latency: &lt; 1ms overhead</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRTOTab = () => (
    <div>
      <div style={styles.section}>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '10px' }}>⏱️</span>
          Recovery Time Objective (RTO)
        </h2>
        
        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>What is RTO?</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '15px' }}>
            <strong>Recovery Time Objective (RTO)</strong> is the maximum acceptable time that your application can be offline 
            after a failure occurs. It answers the question: <em>"How quickly must we restore service?"</em>
          </p>
          
          <div style={{ marginBottom: '15px' }}>
            <a 
              href="https://docs.aws.amazon.com/wellarchitected/latest/reliability-pillar/design-for-recovery-in-the-cloud.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📖 AWS Well-Architected: Design for Recovery →
            </a>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center' as const,
            marginBottom: '20px'
          }}>
            <h4 style={{ marginBottom: '10px' }}>RTO Formula</h4>
            <div style={{ fontSize: '1.2em', fontFamily: 'monospace' }}>
              RTO = Detection Time + Decision Time + Recovery Time + Testing Time
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>RTO Calculator</h3>
          <div style={styles.gridContainer}>
            <div>
              <label style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px', display: 'block' }}>
                Downtime (minutes):
              </label>
              <input
                type="number"
                style={styles.input}
                value={rtoCalculator.downtime}
                onChange={(e) => setRtoCalculator({...rtoCalculator, downtime: parseInt(e.target.value) || 0})}
              />
              
              <label style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px', display: 'block' }}>
                Hourly Business Cost ($):
              </label>
              <input
                type="number"
                style={styles.input}
                value={rtoCalculator.cost}
                onChange={(e) => setRtoCalculator({...rtoCalculator, cost: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div style={{
              background: 'var(--section-bg)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center' as const
            }}>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Impact Analysis</h4>
              <div style={{ fontSize: '2em', color: '#e74c3c', fontWeight: 'bold', marginBottom: '10px' }}>
                ${Math.round((rtoCalculator.downtime / 60) * rtoCalculator.cost).toLocaleString()}
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                Estimated cost of {rtoCalculator.downtime} minutes downtime
              </div>
              
              <div style={{ marginTop: '20px' }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                  Availability: {((1 - (rtoCalculator.downtime / (30 * 24 * 60))) * 100).toFixed(3)}%
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  (Based on 30-day month)
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.gridContainer}>
          {[
            {
              title: '🚀 Near-Zero RTO',
              time: '< 5 minutes',
              cost: '$$$',
              description: 'Active-active multi-region with automatic failover',
              examples: ['Real-time trading', 'Emergency services', 'Critical healthcare systems']
            },
            {
              title: '⚡ Low RTO',
              time: '5-30 minutes', 
              cost: '$$',
              description: 'Hot standby with manual or automatic failover',
              examples: ['E-commerce sites', 'Banking applications', 'SaaS platforms']
            },
            {
              title: '🔄 Medium RTO',
              time: '30 minutes - 4 hours',
              cost: '$',
              description: 'Warm standby or backup restoration',
              examples: ['Internal applications', 'Reporting systems', 'Development environments']
            },
            {
              title: '📚 High RTO',
              time: '4+ hours',
              cost: 'Low',
              description: 'Cold backup restoration or rebuild',
              examples: ['Archive systems', 'Batch processing', 'Non-critical applications']
            }
          ].map((tier, idx) => (
            <div key={idx} style={{
              ...styles.card,
              border: `2px solid ${['#e74c3c', '#f39c12', '#3498db', '#95a5a6'][idx]}`
            }}>
              <h4 style={{ color: ['#e74c3c', '#f39c12', '#3498db', '#95a5a6'][idx], marginBottom: '10px' }}>
                {tier.title}
              </h4>
              <div style={{ marginBottom: '15px' }}>
                <strong style={{ color: 'var(--text-primary)' }}>RTO: {tier.time}</strong>
                <br />
                <span style={{ color: 'var(--text-secondary)' }}>Cost: {tier.cost}</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px', lineHeight: 1.6 }}>
                {tier.description}
              </p>
              <div>
                <strong style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Examples:</strong>
                <ul style={{ color: 'var(--text-muted)', fontSize: '14px', marginLeft: '20px', marginTop: '5px' }}>
                  {tier.examples.map((example, i) => (
                    <li key={i}>{example}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>AWS Services for Different RTO Requirements</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'var(--text-secondary)'
            }}>
              <thead>
                <tr style={{ background: 'var(--section-bg)' }}>
                  <th style={{ padding: '12px', textAlign: 'left' as const, color: 'var(--text-primary)' }}>RTO Target</th>
                  <th style={{ padding: '12px', textAlign: 'left' as const, color: 'var(--text-primary)' }}>AWS Services</th>
                  <th style={{ padding: '12px', textAlign: 'left' as const, color: 'var(--text-primary)' }}>Architecture Pattern</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['< 5 min', 'Route 53, ALB, Auto Scaling, RDS Multi-AZ', 'Active-Active Multi-Region'],
                  ['5-30 min', 'CloudFormation, Lambda, RDS Snapshots', 'Infrastructure as Code + Automation'],
                  ['30 min - 4 hrs', 'EBS Snapshots, AMIs, S3 Cross-Region', 'Backup & Restore with Automation'],
                  ['4+ hrs', 'S3 Glacier, Manual Processes', 'Cold Backup & Manual Recovery']
                ].map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{row[0]}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{row[1]}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border-color)' }}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRPOTab = () => (
    <div>
      <div style={styles.section}>
        <h2 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '10px' }}>💾</span>
          Recovery Point Objective (RPO)
        </h2>
        
        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>What is RPO?</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '15px' }}>
            <strong>Recovery Point Objective (RPO)</strong> is the maximum acceptable amount of data loss measured in time. 
            It answers the question: <em>"How much data can we afford to lose?"</em> This determines your backup frequency.
          </p>
          
          <div style={{ marginBottom: '15px' }}>
            <a 
              href="https://docs.aws.amazon.com/wellarchitected/latest/reliability-pillar/design-for-recovery-in-the-cloud.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px',
                marginRight: '20px'
              }}
            >
              📖 AWS Well-Architected: Recovery Design →
            </a>
            <a 
              href="https://docs.aws.amazon.com/aws-backup/latest/devguide/whatisbackup.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#3498db', 
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              📖 AWS Backup Documentation →
            </a>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center' as const,
            marginBottom: '20px'
          }}>
            <h4 style={{ marginBottom: '10px' }}>RPO Concept</h4>
            <div style={{ fontSize: '1.2em', fontFamily: 'monospace' }}>
              RPO = Time between last backup and failure occurrence
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>RPO Impact Calculator</h3>
          <div style={styles.gridContainer}>
            <div>
              <label style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px', display: 'block' }}>
                Data Loss Window (minutes):
              </label>
              <input
                type="number"
                style={styles.input}
                value={rpoCalculator.dataLoss}
                onChange={(e) => setRpoCalculator({...rpoCalculator, dataLoss: parseInt(e.target.value) || 0})}
              />
              
              <label style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px', display: 'block' }}>
                Records Created Per Hour:
              </label>
              <input
                type="number"
                style={styles.input}
                value={rpoCalculator.hourlyData}
                onChange={(e) => setRpoCalculator({...rpoCalculator, hourlyData: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div style={{
              background: 'var(--section-bg)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center' as const
            }}>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Data Loss Impact</h4>
              <div style={{ fontSize: '2em', color: '#9b59b6', fontWeight: 'bold', marginBottom: '10px' }}>
                {Math.round((rpoCalculator.dataLoss / 60) * rpoCalculator.hourlyData).toLocaleString()}
              </div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Records potentially lost
              </div>
              
              <div style={{
                background: 'var(--card-bg)',
                padding: '15px',
                borderRadius: '8px'
              }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '5px' }}>
                  Backup Frequency Needed
                </div>
                <div style={{ color: '#9b59b6', fontSize: '1.2em', fontWeight: 'bold' }}>
                  Every {rpoCalculator.dataLoss} minutes
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.gridContainer}>
          {[
            {
              title: '🔥 Zero Data Loss',
              rpo: '0 seconds',
              description: 'Synchronous replication to multiple locations',
              methods: ['RDS Multi-AZ', 'DynamoDB Global Tables', 'EFS One Zone-IA'],
              cost: 'Highest',
              useCases: ['Financial transactions', 'Medical records', 'Legal documents']
            },
            {
              title: '⚡ Near-Zero Loss',
              rpo: '< 5 minutes',
              description: 'Continuous or very frequent backups',
              methods: ['RDS Read Replicas', 'ElastiCache Redis', 'Kinesis Data Streams'],
              cost: 'High',
              useCases: ['E-commerce orders', 'User profiles', 'Real-time analytics']
            },
            {
              title: '📊 Low Data Loss',
              rpo: '5-60 minutes',
              description: 'Frequent automated backups',
              methods: ['EBS Snapshots (automated)', 'RDS Automated Backups', 'Aurora Backtrack'],
              cost: 'Medium',
              useCases: ['Business applications', 'Customer data', 'Inventory systems']
            },
            {
              title: '📁 Acceptable Loss',
              rpo: '1-24 hours',
              description: 'Daily or periodic backups',
              methods: ['S3 Cross-Region Replication', 'AWS Backup', 'Manual snapshots'],
              cost: 'Low',
              useCases: ['Reports', 'Archives', 'Development data']
            }
          ].map((tier, idx) => (
            <div key={idx} style={{
              ...styles.card,
              border: `2px solid ${['#e74c3c', '#f39c12', '#3498db', '#95a5a6'][idx]}`
            }}>
              <h4 style={{ color: ['#e74c3c', '#f39c12', '#3498db', '#95a5a6'][idx], marginBottom: '10px' }}>
                {tier.title}
              </h4>
              <div style={{ marginBottom: '15px' }}>
                <strong style={{ color: 'var(--text-primary)' }}>RPO: {tier.rpo}</strong>
                <br />
                <span style={{ color: 'var(--text-secondary)' }}>Cost: {tier.cost}</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px', lineHeight: 1.6 }}>
                {tier.description}
              </p>
              <div style={{ marginBottom: '15px' }}>
                <strong style={{ color: 'var(--text-primary)', fontSize: '14px' }}>AWS Methods:</strong>
                <ul style={{ color: 'var(--text-muted)', fontSize: '14px', marginLeft: '20px', marginTop: '5px' }}>
                  {tier.methods.map((method, i) => (
                    <li key={i}>{method}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Use Cases:</strong>
                <ul style={{ color: 'var(--text-muted)', fontSize: '14px', marginLeft: '20px', marginTop: '5px' }}>
                  {tier.useCases.map((useCase, i) => (
                    <li key={i}>{useCase}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>RPO vs RTO Relationship</h3>
          <div style={{
            background: 'var(--section-bg)',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px'
          }}>
            <h4 style={{ color: 'var(--text-primary)', marginBottom: '15px', textAlign: 'center' as const }}>
              The Trade-off Matrix
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '10px',
                textAlign: 'center' as const
              }}>
                <h5>Low RPO + Low RTO</h5>
                <p style={{ margin: '10px 0', fontSize: '14px' }}>
                  ✅ Maximum protection<br/>
                  ❌ Highest cost<br/>
                  💼 Mission-critical systems
                </p>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '10px',
                textAlign: 'center' as const
              }}>
                <h5>High RPO + Low RTO</h5>
                <p style={{ margin: '10px 0', fontSize: '14px' }}>
                  ⚡ Fast recovery<br/>
                  📊 Some data loss acceptable<br/>
                  💼 Real-time applications
                </p>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '10px',
                textAlign: 'center' as const
              }}>
                <h5>Low RPO + High RTO</h5>
                <p style={{ margin: '10px 0', fontSize: '14px' }}>
                  💾 Data protection priority<br/>
                  ⏳ Longer recovery acceptable<br/>
                  💼 Compliance-driven systems
                </p>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '10px',
                textAlign: 'center' as const
              }}>
                <h5>High RPO + High RTO</h5>
                <p style={{ margin: '10px 0', fontSize: '14px' }}>
                  💰 Lowest cost<br/>
                  ⚠️ Basic protection<br/>
                  💼 Non-critical systems
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'regions', label: 'AWS Regions', component: renderRegionsTab },
    { id: 'azs', label: 'Availability Zones', component: renderAZsTab },
    { id: 'rto', label: 'RTO', component: renderRTOTab },
    { id: 'rpo', label: 'RPO', component: renderRPOTab }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.mainContainer}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={{ fontSize: '2.5em', marginBottom: '10px' }}>
            AWS Architecture Fundamentals
          </h1>
          <p style={{ opacity: 0.8, fontSize: '1.1em' }}>
            Master Regions, Availability Zones, RTO, and RPO for resilient cloud architecture
          </p>
          <button 
            style={styles.themeToggle}
            onClick={toggleTheme}
            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
          >
            <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
            <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
        </div>

        <div style={styles.content}>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {tabs.map(tab => (
              <div
                key={tab.id}
                style={{
                  ...styles.tab,
                  ...(activeTab === tab.id ? styles.activeTab : {})
                }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {/* Tab Content */}
          {tabs.find(tab => tab.id === activeTab)?.component()}
          
          {/* Footer with Additional Resources */}
          <div style={{
            marginTop: '40px',
            padding: '30px',
            background: 'var(--section-bg)',
            borderRadius: '15px',
            textAlign: 'center' as const
          }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>
              📚 Additional AWS Resources
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '15px',
              marginBottom: '20px'
            }}>
              <a 
                href="https://aws.amazon.com/architecture/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#3498db', 
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  padding: '10px',
                  background: 'var(--card-bg)',
                  borderRadius: '8px',
                  display: 'block'
                }}
              >
                🏗️ AWS Architecture Center
              </a>
              <a 
                href="https://docs.aws.amazon.com/whitepapers/latest/aws-overview/introduction.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#3498db', 
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  padding: '10px',
                  background: 'var(--card-bg)',
                  borderRadius: '8px',
                  display: 'block'
                }}
              >
                📖 AWS Overview Whitepaper
              </a>
              <a 
                href="https://aws.amazon.com/training/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#3498db', 
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  padding: '10px',
                  background: 'var(--card-bg)',
                  borderRadius: '8px',
                  display: 'block'
                }}
              >
                🎓 AWS Training & Certification
              </a>
              <a 
                href="https://aws.amazon.com/solutions/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#3498db', 
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  padding: '10px',
                  background: 'var(--card-bg)',
                  borderRadius: '8px',
                  display: 'block'
                }}
              >
                💡 AWS Solutions Library
              </a>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', fontStyle: 'italic' }}>
              Continue your AWS learning journey with these comprehensive resources
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AWSArchitectureGuide;